import { useRef, useState, type ReactNode } from "react";
import type { PriceBreakdown } from "@maestro/pricing";
import { apiMutate, apiMutateMitZusatz } from "../hooks/useApi";
import type { OfferOption } from "./types";
import { money } from "./format";
import { Button, Icon, Modal, Toggle } from "./components";
import {
  A_LA_CARTE_ERKLAERUNG,
  A_LA_CARTE_SCHALTER,
  A_LA_CARTE_UMSTELLEN_TITEL,
  A_LA_CARTE_VOR_ORT,
  FASSUNG_NEU_SENDEN,
  NACH_KARTE,
  aLaCarteUmstellenText,
  istALaCarteOption,
} from "../lib/a-la-carte";
import { formatiereWartezeit } from "./komposition-fortschritt";

/**
 * Varianten-Leiste (Region 4a) — nach Preis sortierte Kacheln (mit inline editierbarem Titel, A3;
 * ohne eigenen Titel zeigt die Kachel ihren Gesamtpreis statt eines Buchstabens, Antoine 11.09.2026),
 * Variante anlegen/duplizieren/löschen, „Mit KI füllen". Aus `builder.tsx`
 * (Schritt 9, Einheit D, Konzept § 2.1/§ 4.9): reiner Ortswechsel (Phase A), kein Schritt B —
 * das Konzept nennt hier keinen Kandidaten für rein lokalen UI-State, alle Handler
 * (`addOption`/`duplicateOption`/`varianteMitKiFuellen`/
 * `setDeleteOptionId`) schreiben in fachlichen Zustand des Parents
 * (`options`/`activeId`/Autosave-Kette, Konzept § 3 Tabelle Zeile 1) und bleiben dort — exakt
 * dasselbe Muster wie `applyPackage`/`onApply` (Schritt 6) und `parseFreeform`/
 * `onParseFreeform` (Schritt 7).
 *
 * `editable` wird UNVERÄNDERT vom Parent übernommen (dort `loadedOptionId != null &&
 * loadedOptionId === activeId`, Konzept § 6.1) — hier nicht neu berechnet.
 *
 * A6 (UX-Polish 24.08.2026): der Save-Status samt „Konflikt – neu laden"-Knopf ist von hier in
 * die Kopfzeile der Seite gezogen (`ui/SpeicherStatus.tsx`) — die frueher hier stehende
 * Begruendung zu `onReloadConflict` gilt dort unveraendert weiter.
 */
export function VariantenLeiste({
  options,
  activeId,
  onSelect,
  option,
  breakdown,
  editable,
  busyOption,
  kiFuellenSeit,
  kiWartetMs,
  entwurfLoeschbar,
  itemCount,
  addOption,
  duplicateOption,
  varianteMitKiFuellen,
  setDeleteOptionId,
  angebotGesperrt = false,
  angebotVersendet = false,
  fassungGesperrt = false,
  onALaCarteGeaendert,
  onOptionChange,
  onTitelGespeichert,
  setError,
}: {
  options: OfferOption[];
  activeId: string | null;
  onSelect: (id: string) => void;
  option: OfferOption | null;
  breakdown: Pick<PriceBreakdown, "amountTotalCents">;
  editable: boolean;
  busyOption: boolean;
  kiFuellenSeit: number | null;
  kiWartetMs: number;
  entwurfLoeschbar: boolean;
  itemCount: number;
  addOption: () => Promise<void>;
  duplicateOption: () => Promise<void>;
  varianteMitKiFuellen: () => Promise<void>;
  setDeleteOptionId: (id: string | null) => void;
  /**
   * Bugfund 01.11.2026: verbindlich angenommen. Neue Varianten/Duplikate sperrt der Server (409 `event_locked`);
   * angenommene Fassung und Nachtrag-Entwurf lassen sich nicht per Mülleimer entfernen (Rückzug über den Banner).
   */
  angebotGesperrt?: boolean;
  /**
   * Status „Angebot gesendet“ (noch nicht angenommen): Änderungen gelten live, die Gastseite zeigt bis zum
   * Neuversand die versendete Fassung. Nach dem Umlegen von „À la carte“ steht dann der Hinweis „Geändert“.
   */
  angebotVersendet?: boolean;
  /** sql/770: der Server meldet die versendete Fassung als gesperrt (`events.offer_fassung_veraltet_seit`). */
  fassungGesperrt?: boolean;
  /** Nach dem Umschalten von „À la carte“: der Parent laedt Vorgang (Sperr-Hinweis) und - wenn Positionen
   *  entfernt wurden - die Variante neu (Positionen, Summe, If-Match-Version). */
  onALaCarteGeaendert?: (positionenEntfernt: boolean) => void;
  /** A3: schreibt den getippten Titel in den `option`-Zustand des Parents (wie bisher im
   *  entfallenen „Variante bearbeiten"-Reiter). */
  onOptionChange: (option: OfferOption) => void;
  /** A3: nach dem erfolgreichen Titel-PATCH — der Parent laedt die Variantenliste neu, damit die
   *  Kachel den neuen Namen auch behaelt, wenn man zu einer anderen Variante wechselt (die Liste
   *  `options` kommt aus einer eigenen Query und wuesste sonst nichts davon). */
  onTitelGespeichert: () => void;
  setError: (message: string) => void;
}) {
  /**
   * A3 (UX-Polish 24.08.2026) — welcher Varianten-Titel gerade im Bearbeiten-Zustand steht.
   * Rein lokaler UI-State dieser Leiste (Konzept § 3): kein anderer Konsument liest ihn, es gibt
   * keine externe Schreibquelle. Der getippte TEXT dagegen ist fachlicher Zustand und lebt wie
   * bisher im `option` des Parents (`onOptionChange`) — genau derselbe Weg, den der entfallene
   * „Variante bearbeiten"-Reiter genommen hat.
   */
  const [titelEditId, setTitelEditId] = useState<string | null>(null);
  /** Der Stand beim Oeffnen — fuer „Escape verwirft". Ohne ihn waere Escape nur ein Zuklappen,
   *  das die halb getippte Aenderung stehen liesse (derselbe Fehler, den „Abbrechen" in der
   *  Zeilenbearbeitung, B-4, ausdruecklich nicht macht). */
  const titelVorherRef = useRef<string>("");

  /**
   * Buchungsstrecke B1 (sql/587) — „Unsere Empfehlung“. Setzt NUR der Betreiber, hier per Klick;
   * der Assistent kann höchstens einen Vorschlag hinterlegen (`recommendationSuggested`), der an
   * der Kachel steht und erst mit diesem Knopf zur Empfehlung wird. Der Server räumt die bisherige
   * Empfehlung desselben Vorgangs selbst ab (höchstens eine je Vorgang).
   */
  const [empfehlungBusy, setEmpfehlungBusy] = useState(false);
  function empfehlungSetzen(neu: boolean) {
    if (!activeId || !option) return;
    setEmpfehlungBusy(true);
    void apiMutate(`/api/offers/${activeId}`, "PATCH", { isRecommended: neu })
      .then(() => {
        onOptionChange({ ...option, isRecommended: neu, recommendationSuggested: false });
        // Dieselbe Neuladung wie nach dem Titel: die übrigen Kacheln verlieren ihre Markierung.
        onTitelGespeichert();
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setEmpfehlungBusy(false));
  }

  /**
   * Migration 731 (Antoine, 19.10.2026) — „À la carte · Abrechnung vor Ort“. Nur der Betreiber setzt
   * das Kennzeichen; es kennzeichnet nur, ein Betrag wird dadurch weder gesetzt noch verlangt. Den
   * Rest (Versand ohne Betrag, Annahme ohne Zahlung) entscheidet der Server.
   */
  const [aLaCarteBusy, setALaCarteBusy] = useState(false);
  /** Variante, deren Kennzeichen nach dem Versand geändert wurde (Hinweis „bitte neu senden“). */
  const [aLaCarteGeaendertId, setALaCarteGeaendertId] = useState<string | null>(null);
  /** Option 2 (Mimmo/Antoine 03.11.2026): offene Rückfrage vor dem Umstellen AUF à la carte, wenn die Variante
   *  Positionen hat - der Server entfernt sie im selben PATCH (`positionenEntfernen`), sonst bliebe ein Betrag stehen
   *  und der Neuversand sperrte mit 422 `a_la_carte_mit_betrag`. */
  const [umstellenFrage, setUmstellenFrage] = useState(false);
  /** sql/770 (Runde 3): ruhiger Hinweis des Servers nach dem Umstellen (z. B. „Überzahlung prüfen“), kein Fehler. */
  const [aLaCarteHinweis, setALaCarteHinweis] = useState<{ id: string; text: string } | null>(null);
  function aLaCarteSetzen(neu: boolean) {
    if (!activeId || !option) return;
    if (neu && itemCount > 0) {
      setUmstellenFrage(true);
      return;
    }
    aLaCarteSpeichern(neu, false);
  }
  function aLaCarteSpeichern(neu: boolean, positionenEntfernen: boolean) {
    if (!activeId || !option) return;
    setALaCarteBusy(true);
    const variante = activeId;
    void apiMutateMitZusatz<unknown, { hinweis?: string }>(`/api/offers/${activeId}`, "PATCH", positionenEntfernen ? { aLaCarte: neu, positionenEntfernen: true } : { aLaCarte: neu })
      .then((antwort) => {
        setALaCarteHinweis(antwort.hinweis ? { id: variante, text: antwort.hinweis } : null);
        onOptionChange({ ...option, aLaCarte: neu });
        if (angebotVersendet) setALaCarteGeaendertId(activeId);
        setUmstellenFrage(false);
        onTitelGespeichert();
        onALaCarteGeaendert?.(positionenEntfernen);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setALaCarteBusy(false));
  }

  function titelSpeichern() {
    setTitelEditId(null);
    if (!activeId || !option) return;
    void apiMutate(`/api/offers/${activeId}`, "PATCH", { title: option.title })
      .then(() => onTitelGespeichert())
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }

  return (
    <>
      {/* Varianten-Vergleich -- mobil horizontal scrollbar (8e), ab lg fixes 3er-Grid (Nicht-anfassen: A/B/C-Vergleich selbst bleibt unveraendert). */}
      <div className="flex gap-2.5 overflow-x-auto pb-1 lg:grid lg:grid-cols-3 lg:overflow-visible lg:pb-0">
        {options.map((o) => {
          const isActive = o.id === activeId;
          // M4: die aktive Karte an denselben Live-Recompute binden wie die Zusammenfassung
          // (breakdown), damit €/Gast sofort nach jeder Positions-/Preis-/Mengen-/Rabatt-
          // Aenderung nachzieht. Inaktive Varianten zeigen weiter ihren gespeicherten Stand.
          const totalCents = isActive ? breakdown.amountTotalCents : o.amountTotalCents;
          const guestCount = isActive ? option?.guestCount ?? o.guestCount : o.guestCount;
          const perGuest = guestCount > 0 ? Math.round(totalCents / guestCount) : totalCents;
          /**
           * A3 (UX-Polish 24.08.2026) — der Titel ist auf der AKTIVEN Kachel per Klick
           * editierbar. Der frühere Weg dahin war der Nachschlage-Reiter „Variante bearbeiten";
           * ein Name, den man in einem Nachschlagewerk suchen muss, wird nicht gepflegt.
           *
           * Nur die aktive Kachel: bei den anderen ist der EINE erwartete Klick „diese Variante
           * auswaehlen". Ein Feld, das dort denselben Klick abfaengt, waere eine Falle. Auf der
           * aktiven Kachel gibt es diese Konkurrenz nicht — sie ist bereits ausgewaehlt.
           *
           * Deshalb sind die beiden Zustaende STRUKTURELL getrennt: inaktiv bleibt die Kachel
           * ein `<button>` (unveraendert), aktiv wird sie ein `<div>` — ein `<input>` in einem
           * `<button>` waere ungueltiges HTML, und jeder Klick ins Feld loeste zugleich den
           * Button aus. Der gemeinsame Inhalt kommt aus `inhalt()`, damit die beiden Zweige
           * nicht auseinanderlaufen koennen.
           */
          const titelEditierbar = isActive && editable && option != null && option.id === o.id;
          const imTitelEdit = titelEditId === o.id && titelEditierbar;
          // Die aktive Kachel zeigt den Titel aus der GELADENEN Variante (`option`), nicht den
          // aus der Listen-Query — sonst zoege der gerade getippte Text nicht mit. Kein Buchstabe
          // mehr als Kennung (Antoine, 11.09.2026): ohne eigenen Titel identifiziert der
          // Gesamtpreis die Kachel - genau die Sortierung, nach der die Kacheln ohnehin stehen.
          // B1: die aktive Kachel liest aus der geladenen Variante (wie der Titel), damit die
          // Markierung sofort nach dem Klick steht.
          const quelle = isActive && option?.id === o.id ? option : o;
          // Migration 731: „À la carte“ hat keinen Betrag - die Kachel sagt es, statt „0,00 €“ zu zeigen.
          const nachKarte = istALaCarteOption(quelle);
          const titelAnzeige = (titelEditierbar ? option.title : o.title) || (nachKarte ? NACH_KARTE : money(totalCents));
          const empfohlen = options.length > 1 && quelle.isRecommended === true;
          const vorgeschlagen = options.length > 1 && !empfohlen && quelle.recommendationSuggested === true;
          const rahmen = `relative block w-full rounded-[11px] p-3 text-left transition-colors ${
            isActive
              ? "border-[1.5px] border-primary bg-primary/[0.07]"
              : "border border-outline-variant/60 bg-surface-container-low hover:bg-surface-container-high/30 dark:border-outline/30 dark:bg-surface-variant/10"
          }`;
          const inhalt = (titel: ReactNode) => (
            <>
              {/* A1 (UX-Polish 24.08.2026): das „AKTIV"-Abzeichen ist ersatzlos entfallen. Die
                  aktive Kachel ist bereits dreifach markiert — Rahmen in Akzentfarbe, farbige
                  Fuellung, fetter Titel — das Woertchen sagte nichts, was man nicht sieht, und
                  belegte die Ecke, in der jetzt nichts mehr steht. */}
              <div className="flex items-center gap-2">
                {o.source === "ai_generated" && (
                  <span title="KI-erzeugter Entwurf">
                    <Icon name="auto_awesome" className="shrink-0 text-[12px] text-primary" />
                  </span>
                )}
                {titel}
              </div>
              {nachKarte ? (
                <p
                  data-testid="builder-varianten-nach-karte"
                  className={`mt-2 text-beleg-notiz font-bold ${isActive ? "text-primary" : "text-on-background dark:text-inverse-on-surface"}`}
                >
                  {NACH_KARTE} <span className="text-[10px] font-medium text-secondary-fixed-dim">· {A_LA_CARTE_VOR_ORT}</span>
                </p>
              ) : (
                <p className={`mt-2 text-beleg-notiz font-bold ${isActive ? "text-primary" : "text-on-background dark:text-inverse-on-surface"}`}>
                  {money(perGuest)} <span className="text-[10px] font-medium text-secondary-fixed-dim">/Gast</span>
                </p>
              )}
              {/* Nachtrag-Version: der Entwurf (Kopie der angenommenen Variante) und die angenommene Fassung daneben sind erkennbar. */}
              {o.nachtragVonOptionId && !o.isChosen && (
                <p data-testid="builder-varianten-nachtrag" className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-primary">
                  <Icon name="edit_note" className="text-[13px]" /> Nachtrag (Entwurf)
                </p>
              )}
              {o.isChosen && options.some((x) => x.nachtragVonOptionId && !x.isChosen) && (
                <p data-testid="builder-varianten-angenommen" className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-secondary-fixed-dim">
                  <Icon name="lock" className="text-[13px]" /> Angenommen
                </p>
              )}
              {empfohlen && (
                <p data-testid="builder-varianten-empfehlung" className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-primary">
                  <Icon name="star" className="text-[13px]" /> Unsere Empfehlung
                </p>
              )}
              {vorgeschlagen && (
                <p
                  data-testid="builder-varianten-vorschlag"
                  className="mt-1 flex items-center gap-1 text-[11px] text-secondary-fixed-dim"
                  title="Der Assistent schlägt diese Variante als Empfehlung vor. Sie gilt erst, wenn du sie übernimmst."
                >
                  <Icon name="auto_awesome" className="text-[12px]" /> Vorschlag: Empfehlung
                </p>
              )}
            </>
          );
          return (
            <div key={o.id} className="group relative w-[160px] shrink-0 lg:w-auto lg:shrink">
            {titelEditierbar ? (
              <div className={rahmen}>
                {inhalt(
                  imTitelEdit ? (
                    <input
                      autoFocus
                      data-testid="builder-varianten-titel-feld"
                      aria-label={`Titel der Variante ${titelAnzeige}`}
                      className="w-full min-w-0 rounded-[6px] border border-primary/50 bg-surface-container-lowest px-1.5 py-0.5 text-beleg font-bold text-on-background outline-none dark:bg-night-card dark:text-inverse-on-surface"
                      placeholder={`z. B. „Klassisch" (${titelAnzeige})`}
                      value={option.title ?? ""}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => onOptionChange({ ...option, title: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                        if (e.key === "Escape") {
                          // Erst den Stand von vor dem Oeffnen zuruecksetzen, DANN schliessen —
                          // ein blosses Zuklappen liesse die halbe Aenderung stehen.
                          onOptionChange({ ...option, title: titelVorherRef.current });
                          setTitelEditId(null);
                        }
                      }}
                      onBlur={titelSpeichern}
                    />
                  ) : (
                    <button
                      type="button"
                      data-testid="builder-varianten-titel"
                      title="Titel der Variante ändern"
                      className="flex min-w-0 flex-1 items-center gap-1 truncate text-left text-beleg font-bold text-on-background hover:underline dark:text-inverse-on-surface"
                      onClick={() => {
                        titelVorherRef.current = option.title ?? "";
                        setTitelEditId(o.id);
                      }}
                    >
                      <span className="truncate">{titelAnzeige}</span>
                      <Icon name="edit" className="shrink-0 text-[13px] opacity-60 transition-opacity group-hover:opacity-100" />
                    </button>
                  ),
                )}
              </div>
            ) : (
              <button type="button" onClick={() => onSelect(o.id)} className={rahmen}>
                {inhalt(
                  <span className={`truncate text-beleg text-on-background dark:text-inverse-on-surface ${isActive ? "font-bold" : ""}`}>
                    {titelAnzeige}
                  </span>,
                )}
              </button>
            )}
            {/* M3: Loeschen pro Varianten-Karte (Sibling zum Button, kein verschachtelter Button).
                IMMER SICHTBAR (30.07.2026, Antoine: "Ich moechte eine Variante auch loeschen
                koennen"): Vorher war der Knopf `opacity-0` und erschien nur bei `group-hover`.
                Auf einem Touch-Geraet gibt es kein Hover - dort war Loeschen damit schlicht
                unerreichbar, und am Rechner nur zufaellig zu finden. Eine Funktion, die man
                nicht sieht, gibt es nicht.
                Letzte Variante (Antoine 12.10.2026: „Dann ist es ein Angebot ohne Version. Ich
                erstelle dann ja eine neue."): im unversendeten Entwurf loescht der Muelleimer
                NUR diese Variante, der Entwurf bleibt und zeigt den leeren Zustand (bis dahin
                fuehrte er zur Loeschung des ganzen Vorgangs). Den ganzen Entwurf loescht
                weiterhin ausschliesslich der eigene Eintrag im …-Menue. Nach dem Versand bleibt
                die letzte Variante gesperrt (Server: 422 last_option_protected). */}
            {(() => {
              const nachtragGeschuetzt = angebotGesperrt && (o.isChosen || o.nachtragVonOptionId != null);
              const gesperrt = nachtragGeschuetzt || (options.length <= 1 && !entwurfLoeschbar);
              return (
                <button
                  type="button"
                  title={
                    nachtragGeschuetzt
                      ? o.isChosen
                        ? "Die angenommene Fassung bleibt erhalten, bis die Kundin einem Nachtrag zustimmt."
                        : "Den Nachtrag-Entwurf ziehst du oben mit „Nachtrag zurückziehen“ zurück."
                      : gesperrt
                        ? "Dieses Angebot wurde bereits versendet – die letzte Variante bleibt erhalten. Nutze \"Stornieren\" auf der Vorgangsseite."
                        : "Variante löschen"
                  }
                  aria-label={
                    nachtragGeschuetzt
                      ? `Variante ${titelAnzeige} kann nicht gelöscht werden`
                      : gesperrt
                      ? `Variante ${titelAnzeige} kann nach dem Versand nicht gelöscht werden`
                      : `Variante ${titelAnzeige} löschen`
                  }
                  onClick={() => {
                    if (!gesperrt) setDeleteOptionId(o.id);
                  }}
                  disabled={busyOption || gesperrt}
                  className="absolute bottom-2 right-2 grid h-6 w-6 place-items-center rounded-full bg-surface-container-high text-on-surface-variant shadow-sm transition-colors hover:bg-error-container hover:text-error disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-surface-container-high disabled:hover:text-on-surface-variant dark:bg-white/[0.08] dark:text-[#b8ae9e] dark:hover:bg-error/20 dark:hover:text-[#e39b92] dark:disabled:hover:bg-white/[0.08]"
                >
                  <Icon name="delete" className="text-[15px]" />
                </button>
              );
            })()}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" className="!px-2" title="Variante hinzufügen" onClick={() => void addOption()} disabled={busyOption || angebotGesperrt}>
          <Icon name="add" /> Variante
        </Button>
        {activeId && (
          <Button variant="ghost" className="!px-2" title="Variante duplizieren" onClick={() => void duplicateOption()} disabled={busyOption || angebotGesperrt}>
            <Icon name="content_copy" className="text-[18px]" />
          </Button>
        )}
        {/* B1: erst ab zwei Varianten gibt es etwas zu empfehlen. */}
        {activeId && editable && option && option.id === activeId && options.length > 1 && (
          <Button
            variant="ghost"
            className="!px-2 !py-1"
            data-testid="builder-empfehlung-knopf"
            title={
              option.isRecommended
                ? "Die Empfehlung von dieser Variante nehmen"
                : "Der Gast sieht diese Variante als „Unsere Empfehlung“, in der Mitte und vorausgewählt – annehmen muss er weiterhin selbst"
            }
            onClick={() => empfehlungSetzen(!option.isRecommended)}
            disabled={busyOption || empfehlungBusy}
          >
            <Icon name="star" className="text-[16px]" />
            {option.isRecommended
              ? "Empfehlung entfernen"
              : option.recommendationSuggested
                ? "Vorschlag übernehmen"
                : "Als Empfehlung markieren"}
          </Button>
        )}
        {/* A1 (UX-Polish 24.08.2026): der zweite „+ Position hinzufügen"-Einstieg NEBEN den Tabs
            ist entfallen. Er oeffnete denselben `PositionHinzufuegenDrawer` wie die Knoepfe in den
            Abschnittskoepfen — nur ohne zu sagen, in WELCHEN Abschnitt die Position faellt (er nahm
            stumm `positionDrawerSection`, also den zuletzt benutzten). Der Drawer bleibt
            unveraendert ueber jeden Abschnittskopf erreichbar; nur dieser mehrdeutige Doppel-
            Einstieg verschwindet. */}
        {/* P9.2: nur auf einer LEEREN Variante. Sobald Positionen darin stehen, wäre
            Füllen ein Überschreiben - der Server lehnt es dann auch ab (409), aber ein
            Knopf, der nur in eine Absage führt, gehört nicht dorthin. */}
        {activeId && editable && itemCount === 0 && (
          <Button
            variant="ghost"
            className="!px-2 !py-1 text-primary"
            title={
              kiFuellenSeit != null
                ? "MAESTRO stellt die Variante zusammen – bisher gemessen: 70–91 Sekunden je Komposition"
                : "Diese leere Variante von MAESTRO füllen lassen – dieselbe Erzeugung wie beim Angebot aus einer Anfrage"
            }
            onClick={() => void varianteMitKiFuellen()}
            disabled={busyOption || kiFuellenSeit != null}
          >
            <Icon name="auto_awesome" className={`text-[16px] ${kiFuellenSeit != null ? "animate-pulse" : ""}`} />
            {kiFuellenSeit != null ? `MAESTRO baut die Variante … ${formatiereWartezeit(kiWartetMs)}` : "✨ Mit KI füllen"}
          </Button>
        )}
        {/* A6 (UX-Polish 24.08.2026): der Autosave-Zustand steht nicht mehr hier, sondern fest
            in der Kopfzeile der Seite (`ui/SpeicherStatus.tsx`, gerendert von `builder.tsx`).
            Neben den Varianten-Tabs war er eine Randnotiz — und genau die beiden Zustaende, auf
            die es ankommt (`conflict`/`error`, „deine Arbeit ist NICHT gespeichert"), gingen dort
            unter. Begruendung vollstaendig im Kopf jener Datei. */}
      </div>
      {/* Migration 731 (Antoine, 19.10.2026): „À la carte · Abrechnung vor Ort“ je Variante. */}
      {activeId && editable && option && option.id === activeId && (
        <div
          data-testid="builder-a-la-carte"
          className="flex items-start justify-between gap-3 rounded-[11px] border border-outline-variant/60 bg-surface-container-low p-3 dark:border-outline/30 dark:bg-surface-variant/10"
        >
          <div className="min-w-0">
            <p className="text-beleg font-bold text-on-background dark:text-inverse-on-surface">{A_LA_CARTE_SCHALTER}</p>
            <p className="mt-0.5 text-[11.5px] text-on-surface-variant dark:text-secondary-fixed-dim">{A_LA_CARTE_ERKLAERUNG}</p>
          </div>
          <Toggle
            checked={option.aLaCarte === true}
            onChange={aLaCarteSetzen}
            label={A_LA_CARTE_SCHALTER}
            disabled={busyOption || aLaCarteBusy}
          />
        </div>
      )}
      {angebotVersendet && (fassungGesperrt || (activeId != null && editable && aLaCarteGeaendertId === activeId)) && (
        <p data-testid="builder-a-la-carte-neu-senden" role="status" className="text-[11.5px] font-bold text-on-background dark:text-inverse-on-surface">
          {FASSUNG_NEU_SENDEN}
        </p>
      )}
      {aLaCarteHinweis && aLaCarteHinweis.id === activeId && (
        <p data-testid="builder-a-la-carte-hinweis" role="status" className="text-[11.5px] text-on-surface-variant">
          {aLaCarteHinweis.text}
        </p>
      )}
      <Modal
        open={umstellenFrage}
        onClose={() => setUmstellenFrage(false)}
        width="440px"
        title={A_LA_CARTE_UMSTELLEN_TITEL}
        testId="a-la-carte-umstellen-dialog"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setUmstellenFrage(false)} disabled={aLaCarteBusy}>
              Abbrechen
            </Button>
            <Button type="button" variant="primary" onClick={() => aLaCarteSpeichern(true, true)} disabled={aLaCarteBusy}>
              Umstellen
            </Button>
          </>
        }
      >
        <p className="font-body-sm text-body-sm text-on-surface-variant">{aLaCarteUmstellenText(itemCount)}</p>
      </Modal>
    </>
  );
}
